import {
  isNoteLine,
  type LuachDay,
  type LuachEntry,
  rowKey,
  SHKIA_LABEL,
  scheduledDays,
  sheetAsides,
  sheetTitleHe,
  withSunset,
  YearTable,
} from '@app/luach'
import { plain } from '@app/stationery'
import '@app/stationery/stationery.css'
import '@app/luach/yeartable.css'
import {
  type PublicLanguage,
  type PublicScript,
  say,
  spelled,
} from '@app/shared'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download, Printer } from 'lucide-react'
import { useEffect, useState } from 'react'
import { CURRENT_HEBREW_YEAR } from '../lib/hebrew-year'
import type { OrgListItem } from '../lib/org'
import { pdfUrl, yearTableUrl } from '../lib/pdf'
import { printPdf } from '../lib/print'
import { useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { Button, Notice } from '../ui'
import { Said } from '../ui/Said'
import { SectionNav } from '../ui/SectionNav'
import {
  civilSpan,
  headingDates,
  hebrewYearOf,
  hebrewYearOfOccurrence,
  landing,
  minyanEn,
  nightOf,
  readerDays,
  readerTabs,
} from './public-schedule'
import { YearTableContainer } from './ScheduleScreen'
import './public-schedule.css'

/** The shipped year sheet a visitor is offered — the one the office prints. */
const YEAR_PRESET = 'shabbosim'

/**
 * A congregation's schedule, set for somebody reading it.
 *
 * The office operates a schedule and this page reads one, so the two have
 * their own layouts — and still cannot show different times, because both
 * render the same `occasion.resolve` result and this page computes none. Which
 * days a sheet lists, where shkia sits among them and what a composite line
 * prints are `@app/luach`'s answers, taken whole, so the grid and the printed
 * sheet agree line for line.
 *
 * The tabs are the shul's own occasions. Print and PDF hand over the sheet the
 * office prints, on the shul's own stationery.
 */
export function PublicSchedule({ shul }: { shul: OrgListItem }) {
  const trpc = useTRPC()
  const spaceId = shul.spaceId
  const today = useToday(shul.timeZoneId)
  const lang = shul.publicLanguage

  /* The sheet being read: an occasion and the first day of one occurrence of
     it. Null until the reader picks, so the page opens on the calendar's own
     answer to "what is next". */
  const [picked, setPicked] = useState<{
    occasionKey: string
    from: string | null
  } | null>(null)
  /* Which Hebrew year the stepper is walking, and which end of it to land on
     once a step has run off the year it was in. */
  const [year, setYear] = useState<number | null>(null)
  const [edge, setEdge] = useState<'first' | 'last' | null>(null)
  // Every Shabbos of the year at once, rather than one week's sheet.
  const [wholeYear, setWholeYear] = useState(false)

  const occasions = useQuery(trpc.occasion.list.queryOptions({ spaceId }))
  const tabs = readerTabs(occasions.data ?? [])

  /* The year before as well as this one: the browser's guess at the current
     Hebrew year runs ahead through Elul, and the Shabbos a reader wants then is
     still in the year it names as last. */
  const timelineBefore = useQuery({
    ...trpc.occasion.timeline.queryOptions({
      spaceId,
      hebrewYear: CURRENT_HEBREW_YEAR - 1,
    }),
    enabled: picked === null,
  })
  const timeline = useQuery({
    ...trpc.occasion.timeline.queryOptions({
      spaceId,
      hebrewYear: CURRENT_HEBREW_YEAR,
    }),
    enabled: picked === null,
  })
  const timelineReady = timelineBefore.isSuccess && timeline.isSuccess
  const landed =
    picked === null && timelineReady && occasions.data
      ? landing(
          [...(timelineBefore.data ?? []), ...(timeline.data ?? [])],
          occasions.data,
          today,
        )
      : null

  const occasionKey =
    picked?.occasionKey ?? landed?.occasionKey ?? tabs[0]?.key ?? 'shabbos'
  const isWeekly = tabs.find((t) => t.key === occasionKey)?.cadence === 'weekly'
  const showYear = wholeYear && isWeekly

  /* A tab opens on the occurrence under way or next — asked of `occurrences`
     from today, which names a run by its first day even from inside it. */
  const current = useQuery({
    ...trpc.occasion.occurrences.queryOptions({
      spaceId,
      occasionKey,
      from: today,
      count: 1,
    }),
    enabled: picked ? picked.from === null : timelineReady && !landed,
  })
  const from =
    picked?.from ?? landed?.startDate ?? current.data?.[0]?.startDate ?? null

  // The year the list is walked in: the one a step chose, else the one the
  // calendar names for the occurrence being read.
  const landedOn = picked?.from ? null : (landed ?? current.data?.[0] ?? null)
  const listYear =
    year ??
    (landedOn ? hebrewYearOfOccurrence(landedOn) : null) ??
    CURRENT_HEBREW_YEAR
  const occurrences = useQuery({
    ...trpc.occasion.occurrences.queryOptions({
      spaceId,
      occasionKey,
      hebrewYear: listYear,
      count: 120,
    }),
  })
  const list = occurrences.data ?? []

  // After a step past the year's edge, land on the adjacent year's first or
  // last occurrence once its list has arrived.
  useEffect(() => {
    if (!edge || list.length === 0) return
    const target = edge === 'last' ? list[list.length - 1] : list[0]
    setPicked({ occasionKey, from: target.startDate })
    setEdge(null)
  }, [edge, list, occasionKey])

  const step = (delta: 1 | -1) => {
    if (showYear) {
      setYear(listYear + delta)
      return
    }
    /* The adjacent occurrence by date rather than by position, so a sheet
       whose start the year's list does not hold still steps to its neighbor. */
    const target = !from
      ? undefined
      : delta > 0
        ? list.find((o) => o.startDate > from)
        : list.findLast((o) => o.startDate < from)
    if (target) {
      setPicked({ occasionKey, from: target.startDate })
      setYear(listYear)
      return
    }
    setYear(listYear + delta)
    setEdge(delta < 0 ? 'last' : 'first')
  }

  const choose = (key: string) => {
    setPicked({ occasionKey: key, from: null })
    setYear(null)
    setEdge(null)
    setWholeYear(false)
  }

  const schedule = useQuery({
    ...trpc.occasion.resolve.queryOptions({
      spaceId,
      occasionKey,
      from: from ?? '',
    }),
    enabled: !!from && !showYear,
  })

  const shapes = useQuery({
    ...trpc.occasion.yearShapes.queryOptions({ spaceId }),
    enabled: showYear,
  })
  const orientation =
    shapes.data?.presets.find((p) => p.key === YEAR_PRESET)?.orientation ??
    'landscape'
  const yearSheet = useQuery({
    ...trpc.occasion.year.queryOptions({
      spaceId,
      hebrewYear: listYear,
      preset: YEAR_PRESET,
    }),
    enabled: showYear,
  })

  /*
   * The printed sheet, addressed exactly as the office addresses it with its
   * options at rest: no English gloss, the Hebrew date alone, the shul's own
   * letterhead, the schedule rather than its reasoning.
   */
  const sheetUrl = showYear
    ? yearTableUrl({
        spaceId,
        year: listYear,
        preset: YEAR_PRESET,
        orientation,
      })
    : from
      ? pdfUrl({
          spaceId,
          occasionKey,
          from,
          letterhead: shul.letterhead,
        })
      : null
  const sheetReady = !!sheetUrl && !!(showYear ? yearSheet.data : schedule.data)

  const result = showYear ? null : schedule.data
  const days = result ? scheduledDays(result.days) : []
  const occasionEn = tabs.find((t) => t.key === occasionKey)?.labelEn ?? ''
  const titleEn = showYear
    ? `Every ${occasionEn}`
    : isWeekly
      ? [occasionEn, list.find((o) => o.startDate === from)?.labelEn]
          .filter((part, i, all) => part && all.indexOf(part) === i)
          .join(' ')
      : (result?.labelEn ?? occasionEn)
  const titleHe = showYear
    ? 'כל השבתות'
    : result
      ? plain(sheetTitleHe(result))
      : ''
  const asides = result ? sheetAsides(result) : null
  /* Every read the sheet waits on, so a failure anywhere along the way says so
     rather than leaving the page loading. */
  const lookupError =
    timelineBefore.error ??
    timeline.error ??
    current.error ??
    occurrences.error ??
    schedule.error
  const nothingAhead = !from && current.isSuccess && current.data.length === 0
  const subtitleEn = showYear ? String(listYear) : civilSpan(days)
  const subtitleHe = showYear
    ? (yearSheet.data?.hebrewYearHe ?? '')
    : result
      ? hebrewYearOf(result)
      : ''
  const title = say(lang.occasions, titleEn, titleHe)
  /* The line under the title: the span in the script chosen for dates, then
     what this week is — a special Shabbos, Shabbos Mevorchim — in the one
     chosen for occasion names. Each script's half is one line of its own. */
  const occurrence = list.find((o) => o.startDate === from)
  const weekEn = [
    ...(isWeekly ? (occurrence?.specialShabbosEn ?? []) : []),
    ...(result?.days.some((d) => d.mevorchim)
      ? [spelled('Shabbos Mevorchim', shul.calendarSpelling)]
      : []),
  ]
  const span = say(lang.dates, subtitleEn, subtitleHe)
  const week = say(
    lang.occasions,
    weekEn.join(' · '),
    (asides?.week ?? []).join(' · '),
  )
  const underEn = [span.en, week.en].filter(Boolean).join(' · ')
  const underHe = [span.he, week.he].filter(Boolean).join(' · ')

  return (
    <div className="pubsched">
      {/* Each line is in the scripts the shul chose, English leading and the
          Hebrew beside it where there are both: the occasion's names, then
          what qualifies them. */}
      <header className="pubsched__head">
        <div className="pubsched__pair">
          {title.en ? (
            <h1 className="pubsched__title">{title.en}</h1>
          ) : (
            <h1 className="pubsched__title said-he" lang="he" dir="rtl">
              {title.he}
            </h1>
          )}
          {title.en && title.he ? (
            <span className="pubsched__title pubsched__he" lang="he" dir="rtl">
              {title.he}
            </span>
          ) : null}
        </div>
        <div className="pubsched__pair">
          {underEn ? <p className="pubsched__span">{underEn}</p> : null}
          {underHe ? (
            <span
              className="pubsched__span pubsched__hebrew"
              lang="he"
              dir="rtl"
            >
              {underHe}
            </span>
          ) : null}
        </div>
      </header>

      <div className="pubsched__bar">
        {tabs.length > 0 ? (
          <SectionNav
            variant="public"
            label="Occasions"
            tabs={tabs.map((t) => ({
              key: t.key,
              label: <Said said={say(lang.occasions, t.labelEn, t.labelHe)} />,
            }))}
            active={occasionKey}
            onChange={choose}
          />
        ) : null}
        <div className="pubsched__actions">
          <Button
            variant="secondary"
            disabled={!sheetReady}
            onClick={() => {
              if (sheetUrl) void printPdf(sheetUrl)
            }}
          >
            <Printer className="btn__icon" aria-hidden />
            Print
          </Button>
          <Button
            variant="secondary"
            disabled={!sheetReady}
            onClick={() => {
              if (sheetUrl) window.open(sheetUrl, '_blank', 'noopener')
            }}
          >
            <Download className="btn__icon" aria-hidden />
            PDF
          </Button>
        </div>
      </div>

      {/* Walking the calendar is the rarer act, so it is a quiet line under
          the strip rather than a control beside the tabs. */}
      <div className="pubsched__walk">
        <Button
          variant="quiet"
          aria-label={showYear ? 'Previous year' : `Previous ${occasionEn}`}
          onClick={() => step(-1)}
        >
          <ChevronLeft className="btn__icon" aria-hidden />
          Previous
        </Button>
        {isWeekly ? (
          <Button
            variant="quiet"
            onClick={() => {
              setWholeYear(!showYear)
              setYear(listYear)
            }}
          >
            {showYear ? `One ${occasionEn}` : `Every ${occasionEn} of the year`}
          </Button>
        ) : null}
        <Button
          variant="quiet"
          aria-label={showYear ? 'Next year' : `Next ${occasionEn}`}
          onClick={() => step(1)}
        >
          Next
          <ChevronRight className="btn__icon" aria-hidden />
        </Button>
      </div>

      {occasions.error ? (
        <Notice tone="error">{occasions.error.message}</Notice>
      ) : null}
      {!showYear && lookupError ? (
        <Notice tone="error">{lookupError.message}</Notice>
      ) : null}
      {!showYear && !result && !lookupError ? (
        <Notice>
          {nothingAhead
            ? `No ${occasionEn} is on the calendar ahead.`
            : 'Loading the schedule…'}
        </Notice>
      ) : null}

      {result ? (
        <section className="pubsched__days" aria-label="Days">
          {readerDays(result.days).map(({ day, through }) => (
            <Day key={day.date} day={day} through={through} language={lang} />
          ))}
          {asides &&
          (asides.months.length > 0 || asides.overrides.length > 0) ? (
            <div className="pubsched__asides">
              {asides.months.map((m) => (
                <p className="pubsched__hebrew" lang="he" key={m.key}>
                  {m.roshChodesh ? <span>{m.roshChodesh}</span> : null}
                  <span>{m.molad}</span>
                </p>
              ))}
              {asides.overrides.map((n) => (
                <p key={n}>
                  <span aria-hidden>*</span> {n}
                </p>
              ))}
            </div>
          ) : null}
          {result.greetingHe || shul.city ? (
            <div className="pubsched__close">
              {result.greetingHe ? (
                <span className="pubsched__greeting pubsched__hebrew" lang="he">
                  {plain(result.greetingHe)}
                </span>
              ) : null}
              {shul.city ? (
                <span className="pubsched__where">
                  Times are for {shul.city}.
                </span>
              ) : null}
            </div>
          ) : null}
        </section>
      ) : null}

      {showYear ? (
        <>
          {yearSheet.isLoading ? <Notice>Building the year…</Notice> : null}
          {yearSheet.error ? (
            <Notice tone="error">{yearSheet.error.message}</Notice>
          ) : null}
          {yearSheet.data ? (
            <YearTableContainer key={`${listYear}-${orientation}`}>
              <YearTable
                shul={shul}
                titleHe={`לוח זמנים ${yearSheet.data.hebrewYearHe}`}
                columns={yearSheet.data.columns}
                rows={yearSheet.data.rows}
                orientation={orientation}
                showSubtitle={false}
                print
              />
            </YearTableContainer>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

/**
 * One day of the sheet: its name and its date, each in the script the shul
 * chose, under a heavy rule, then its lines in the order the printed sheet sets
 * them — shkia slotted where it falls.
 */
function Day({
  day,
  through,
  language,
}: {
  day: LuachDay
  /** The last day a block modeled once stands for, when it covers several. */
  through: LuachDay | null
  language: PublicLanguage
}) {
  const dates = headingDates(day, through)
  const name = say(language.occasions, day.dayLabelEn, plain(day.dayLabelHe))
  return (
    <section className="pubsched__day">
      <div className="pubsched__dayhead">
        <div className="pubsched__dayline">
          {name.en ? (
            <h2 className="pubsched__dayname">{name.en}</h2>
          ) : (
            <h2 className="pubsched__dayname said-he" lang="he" dir="rtl">
              {name.he}
            </h2>
          )}
          {name.en && name.he ? (
            <span
              className="pubsched__daynamehe pubsched__he"
              lang="he"
              dir="rtl"
            >
              {name.he}
            </span>
          ) : null}
        </div>
        <div className="pubsched__dates">
          <Said said={say(language.dates, dates.civil, dates.hebrew)} />
        </div>
      </div>
      {withSunset(day).map((item) =>
        item.kind === 'anchor' ? (
          <div className="pubsched__row" key="shkia">
            <span className="pubsched__label">
              <Said
                said={say(language.lines, SHKIA_LABEL.en, SHKIA_LABEL.he)}
              />
            </span>
            {/* The time every other one on the day is measured from: the value
                worth finding, so the figure takes the accent and the row does
                not. */}
            <span className="pubsched__value pubsched__value--shkia">
              <Time value={item.time} />
            </span>
          </div>
        ) : (
          <Line
            key={rowKey(day.date, item.entry)}
            entry={item.entry}
            night={nightOf(day, item.entry)}
            script={language.lines}
          />
        ),
      )}
    </section>
  )
}

/** A line as the sheet prints it, named in the script the shul chose: a note, a phrase, a time or a composite's opinions. */
function Line({
  entry,
  night,
  script,
}: {
  entry: LuachEntry
  /** The night a carried line happens on, where its section also holds day. */
  night: string | null
  /** Which script the shul's site names tefillos and schedule lines in. */
  script: PublicScript
}) {
  if (isNoteLine(entry)) {
    return (
      <div className="pubsched__row pubsched__row--note">
        <span>{entry.noteText}</span>
      </div>
    )
  }
  const parts = entry.parts?.length ? entry.parts : null
  const phrase = entry.displayText ?? entry.noteText
  const which = [night, minyanEn(entry.minyanIndex)].filter(Boolean).join(' · ')
  return (
    <div className="pubsched__row">
      <span className="pubsched__label">
        <Said
          said={say(
            script,
            withParts(
              entry.labelEn,
              parts?.map((p) => p.labelEn),
            ),
            withParts(
              entry.labelHe,
              parts?.map((p) => p.labelHe),
            ),
          )}
        />
        {which || entry.room ? (
          <span className="pubsched__qualifier">
            {which}
            {which && entry.room ? ' · ' : null}
            {/* A room is named in whichever script the shul says it in, so it
                is isolated from the English around it. */}
            {entry.room ? <bdi>{entry.room.name}</bdi> : null}
          </span>
        ) : null}
        {entry.subText ? (
          <span className="pubsched__qualifier">{entry.subText}</span>
        ) : null}
      </span>
      <span className="pubsched__value">
        {parts ? (
          <span className="pubsched__time">
            {parts.map((p, i) => (
              <span key={p.key}>
                {i > 0 ? <span className="pubsched__sep">/</span> : null}
                {p.time ? <Time value={p.time} /> : '—'}
              </span>
            ))}
          </span>
        ) : entry.time && !phrase ? (
          <Time value={entry.time} />
        ) : (
          <span className="pubsched__phrase pubsched__hebrew" lang="he">
            {phrase}
          </span>
        )}
        {/* A time replacing the usual one carries the sheet's asterisk; its
          reason closes the page, as it closes the paper. */}
        {entry.overrideNote ? (
          <span className="pubsched__mark" aria-hidden>
            *
          </span>
        ) : null}
      </span>
    </div>
  )
}

/**
 * A line's name with its opinions after it — "Sof zman krias shema (MGA /
 * GRA)" — in one script. A name the line lacks in that script leaves nothing,
 * so the other script stands in rather than a bare list of opinions.
 */
const withParts = (label: string, parts?: string[]) =>
  label.trim() && parts?.length ? `${label} (${parts.join(' / ')})` : label

/** "6:43 PM" with the meridiem set small: the hour is the fact, AM/PM only qualifies it. */
function Time({ value }: { value: string }) {
  const m = /^(\d{1,2}:\d{2})\s*(AM|PM)$/i.exec(value.trim())
  return (
    <span className="pubsched__time">
      {m ? (
        <>
          {m[1]}
          <span className="pubsched__meridiem">{m[2].toUpperCase()}</span>
        </>
      ) : (
        value
      )}
    </span>
  )
}
